import { LaunchVideoComponent } from './launch-video/launch-video.component';
import { HomeTripState } from './trips/home-trip-state.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import {
  ActivationStart,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { NgxGoogleAnalyticsModule } from '@hakimio/ngx-google-analytics';
import { DeviceDetectorService } from 'ngx-device-detector';
import {
  BehaviorSubject,
  Observable,
  Subject,
  takeUntil,
  tap
} from 'rxjs';

import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { DirectivesModule } from './directives/directives.module';
import { DirectoryComponent } from './directory/directory.component';
import { FlixbusPromotionComponent } from './flixbus-promotion/flixbus-promotion.component';
import { FooterComponent } from './footer/footer.component';
import { HeaderComponent } from './header/header.component';
import { DeviceService } from './interceptors/device.service';
import { InviteService } from './invite.service';
import { LocaleButtonComponent } from './locale-button/locale-button.component';
import { LocaleModalComponent } from './locale-modal/locale-modal.component';
import { LocaleService } from './locale.service';
import { LoginService } from './login.service';
import { LogoComponent } from './logo/logo.component';
import { MobileHeaderComponent } from './mobile-header/mobile-header.component';
import { MobileMenuComponent } from './mobile-menu/mobile-menu.component';
import { NavigationService } from './navigation.service';
import { PageComponent } from './page/page.component';
import { ScrollService } from './scroll.service';
import { SeoComponent } from './seo/seo.component';
import { ApiService } from './services/api/api.service';
// import { SwService } from './sw.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    LaunchVideoComponent,
    RouterLink,
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    FormsModule,
    AngularSvgIconModule,
    DirectoryComponent,
    LocaleModalComponent,
    DirectivesModule,
    LogoComponent,
    SeoComponent,
    NgxGoogleAnalyticsModule,
    PageComponent,
    LocaleButtonComponent,
    MobileHeaderComponent,
    MobileMenuComponent,
    FlixbusPromotionComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ transform: 'translateY(10px)', opacity: 0 }),
        animate(
          '0.17s ease-out',
          style({ transform: 'translateY(0)', opacity: 1 })
        ),
      ]),
      transition(':leave', [
        style({ transform: 'translateY(0)', opacity: 1 }),
        animate(
          '0.17s ease-out',
          style({ transform: 'translateY(10px)', opacity: 0 })
        ),
      ]),
    ]),
  ],
})
export class AppComponent implements OnInit {
  public login!: Observable<boolean>;
  public showLocaleModal$!: Observable<boolean>;

  public desktop = new BehaviorSubject(false);

  private destroyed = new Subject<void>();
  public showFooter = new BehaviorSubject(true);
  public showMobileHeader = new BehaviorSubject(true);
  public showHeader = new BehaviorSubject(true);
  public showPromotion = new BehaviorSubject(false);
  public isMobileMenuOpen = new BehaviorSubject(false);

  public email = '';

  constructor(
    public homeTrip: HomeTripState,
    private deviceDetector: DeviceDetectorService,
    private deviceService: DeviceService,
    private loginService: LoginService,
    private responsive: BreakpointObserver,
    private router: Router,
    private apiService: ApiService,
    private titleService: Title,
    private metaService: Meta,
    private inviteService: InviteService,
    private cd: ChangeDetectorRef,
    private localeService: LocaleService,
    @Inject(PLATFORM_ID) private platformId: string,
    // private swService: SwService
    private navigationService: NavigationService,
    private scrollService: ScrollService
  ) {}

  get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  ngOnInit() {
    this.navigationService.init();
    // this.swService.init();
    this.login = this.loginService.login$;
    this.showLocaleModal$ = this.localeService.showLocaleModal$;

    if (this.deviceDetector.isDesktop()) {
      this.deviceService.setDeviceType('3');
    } else {
      this.deviceService.setDeviceType('2');
    }
    this.deviceService.setUserAgent(this.deviceDetector.userAgent);
    // if (isPlatformBrowser(this.platformId)) {
    this.localeService.init();
    // }

    this.router.events.pipe(takeUntil(this.destroyed)).subscribe((data) => {
      if (data instanceof ActivationStart) {
        // Close mobile menu on route change
        this.closeMobileMenu();
        
        const d = Object.assign({}, ...data.snapshot.pathFromRoot.map(route => route.data));
        const ownerLaunch = data.snapshot.queryParamMap.get('utm_source') === 'owner_email'
          || ['partner', 'multileg'].includes(data.snapshot.queryParamMap.get('watch') || '');
        this.showPromotion.next(d['showPromotion'] !== false && !ownerLaunch);

        if (d.hasOwnProperty('showFooter') && !d['showFooter']) {
          this.showFooter.next(false);
        } else {
          this.showFooter.next(true);
        }

        if (d.hasOwnProperty('showMobileHeader') && !d['showMobileHeader']) {
          this.showMobileHeader.next(false);
        } else {
          this.showMobileHeader.next(true);
        }
        // const currentRoute = data.snapshot.routeConfig?.path;
        // this.showHeader.next(!currentRoute?.includes('affiliate-program'));

        if (d.hasOwnProperty('metaTags')) {
          Object.entries(d['metaTags']).forEach((tag: any) => {
            this.metaService.updateTag({ name: tag[0], content: tag[1] });
          });

          if (!d['metaTags']['og:image']) {
            this.metaService.removeTag("name='og:image'");
          }
        }
      }
    });

    this.responsive
      .observe([
        Breakpoints.XSmall,
        Breakpoints.Small,
        Breakpoints.Medium,
        '(max-width: 744px)',
      ])
      .pipe(takeUntil(this.destroyed))
      .subscribe((result) => {
        // TODO if on mobile explore and expanding to desktop, just take to desktop home
        if (result.matches) {
          this.desktop.next(false);
        } else {
          this.desktop.next(true);
        }
      });

    // Handle body scroll lock when mobile menu is open
    if (this.isBrowser) {
      this.isMobileMenuOpen.pipe(takeUntil(this.destroyed)).subscribe(isOpen => {
        if (isOpen) {
          document.body.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = '';
        }
      });
    }
  }

  inviteBackpacker() {
    if (this.email) {
      this.inviteService
        .inviteBackpacker(this.email)
        .pipe(
          tap((res) => {
            this.email = 'Success!';
            this.cd.detectChanges();
          })
        )
        .subscribe();
    }
  }

  ngOnDestroy() {
    // Reset body overflow
    if (this.isBrowser) {
      document.body.style.overflow = '';
    }
    
    this.destroyed.next();
    this.destroyed.complete();
  }

  public close() {
    this.loginService.hideLogin();
  }

  public closeLocaleModal() {
    this.localeService.closeLocaleModal();
  }

  public toggleMobileMenu() {
    this.isMobileMenuOpen.next(!this.isMobileMenuOpen.value);
  }

  public closeMobileMenu() {
    this.isMobileMenuOpen.next(false);
  }
}
